import GoogleSignInButton from "@/components/auth/GoogleSignInButton";
import { API_BASE_URL } from "@/env";
import { useAuth } from "@/contexts/AuthContext";
import { FormEvent, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router";

export default function LoginPage() {
  const { isAuthenticated, loading, loginWithPassword } = useAuth();
  const location = useLocation();
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const nextPath =
    (location.state as { next?: string } | null)?.next ||
    new URLSearchParams(location.search).get("next") ||
    "/";

  const backendLoginUrl = useMemo(() => {
    const frontendReturnUrl = new URL("/login", window.location.origin);
    frontendReturnUrl.searchParams.set("next", nextPath);
    return `${API_BASE_URL}/api-auth/login/?next=${encodeURIComponent(frontendReturnUrl.toString())}`;
  }, [nextPath]);

  const handlePasswordLogin = async (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    if (!usernameOrEmail.trim() || !password.trim()) {
      setLoginError("Please enter both username/email and password.");
      return;
    }

    try {
      setIsSubmitting(true);
      setLoginError(null);
      await loginWithPassword(usernameOrEmail.trim(), password);
    } catch (err) {
      setLoginError(
        err instanceof Error
          ? err.message
          : "Unable to log in. Please check your credentials and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={nextPath} replace />;
  }

  return (
    <div className="flex items-center justify-center py-8">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Sign in to ClarifAI
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Use Google, or sign in with your ClarifAI username/email and password.
          </p>
        </div>

        <div className="flex justify-center">
          <GoogleSignInButton
            className="w-full max-w-xs"
            text="Sign in with Google"
          />
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">or</span>
          </div>
        </div>

        <form onSubmit={handlePasswordLogin} className="space-y-4">
          <div>
            <label htmlFor="username-or-email" className="block text-sm font-medium text-gray-700">
              Username or Email
            </label>
            <input
              id="username-or-email"
              type="text"
              autoComplete="username"
              value={usernameOrEmail}
              onChange={(evt) => setUsernameOrEmail(evt.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(evt) => setPassword(evt.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              placeholder="Enter your password"
            />
          </div>

          {loginError && <p className="text-sm text-red-600">{loginError}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-md bg-gray-900 px-4 py-2 text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Signing in..." : "Sign in with password"}
          </button>
        </form>

        <p className="text-center text-xs text-gray-500">
          Having trouble with in-app login?{" "}
          <a href={backendLoginUrl} className="text-blue-600 hover:text-blue-700 underline">
            Open Django login page
          </a>
          .
        </p>
      </div>
    </div>
  );
}
